import { makeEsArticle } from "./learnFactoryEs";
import type { LearnFAQ } from "@/pages/learn/learnShared";
import type { RelatedLink as RL } from "@/components/RelatedPages";

const successRateRelated: RL[] = [
  {
    title: "SAINT® vs TMS acelerada guiada por fMRI",
    href: "/es/learn/saint-vs-accelerated-tms",
    description:
      "Qué es igual, qué no, y qué cubren la autorización de la FDA y los datos del ensayo",
  },
  {
    title: "¿Cuánto duran los resultados de SAINT®?",
    href: "/es/learn/what-happens-after-saint-tms",
    description:
      "Durabilidad, datos de seguimiento y mantenimiento después de SAINT®",
  },
  {
    title: "¿Con qué rapidez actúa SAINT® TMS?",
    href: "/es/learn/how-fast-does-saint-tms-work",
    description:
      "Calendario de respuesta durante y después del protocolo de 5 días",
  },
  {
    title: "¿Y si SAINT® no funciona?",
    href: "/es/learn/what-if-saint-doesnt-work",
    description:
      "Próximos pasos y opciones si SAINT® no produce alivio suficiente",
  },
  {
    title: "SAINT® TMS acelerada",
    href: "/es/saint",
    description: "El protocolo de 5 días guiado por fMRI que ofrecemos",
  },
  {
    title: "¿Qué es la depresión resistente al tratamiento?",
    href: "/es/learn/what-is-treatment-resistant-depression",
    description: "Definición, causas y opciones de tratamiento",
  },
];

const faqs: LearnFAQ[] = [
  {
    question: "¿Cuál es la tasa de éxito de SAINT®?",
    answer:
      "En el ensayo aleatorizado de 2022, el 79 % de los participantes alcanzó remisión tras el protocolo de SAINT® de cinco días (Cole et al., Am J Psychiatry 2022;179(2):132–141). Los resultados individuales pueden variar. Estudios abiertos previos reportaron tasas de remisión similares o más altas, pero los diseños abiertos no controlan el efecto placebo como lo hace un ensayo aleatorizado. La cifra del 79 % es el resultado publicado más riguroso para el sistema SAINT® específicamente.",
  },
  {
    question: "¿Qué significa remisión en los estudios de SAINT®?",
    answer:
      "Remisión significa que la puntuación de depresión de un participante cayó por debajo de un umbral en una escala de ánimo estandarizada; en los ensayos de SAINT®, la Escala de Depresión Montgomery-Åsberg (MADRS). Una puntuación MADRS de 10 o menos generalmente indica remisión. La remisión es distinta de la respuesta, que significa una mejoría notable que no necesariamente alcanza el umbral de remisión. La remisión es la medida más estricta y más completa.",
  },
  {
    question: "¿Cómo se compara SAINT® con la TMS normal?",
    answer:
      "La TMS estándar suele administrar una sesión al día durante 6 semanas. Las tasas de remisión publicadas para la TMS estándar varían entre aproximadamente 30 % y 40 % en diversos estudios. SAINT® comprime el tratamiento en 5 días con 10 sesiones al día y usa orientación personalizada por fMRI. En el ensayo aleatorizado de 2022, el 79 % de los participantes alcanzó remisión (Cole et al., 2022). Los resultados individuales pueden variar. Los protocolos difieren en calendario, orientación y la evidencia detrás de cada sistema.",
  },
  {
    question:
      "¿Las tasas de éxito de SAINT® provienen de un ensayo clínico real?",
    answer:
      "Sí. El resultado clave (79 % de remisión) proviene de un ensayo aleatorizado doble ciego publicado en el American Journal of Psychiatry en 2022 (Cole et al., 2022;179(2):132–141). El estudio comparó SAINT® con un control simulado (placebo). Estudios abiertos previos del mismo grupo de Stanford reportaron tasas similares, pero el ensayo de 2022 es la evidencia publicada más sólida porque fue aleatorizado y con control simulado.",
  },
  {
    question: "¿Los resultados de SAINT® se aplican a otra TMS acelerada?",
    answer:
      "No. La cifra del 79 % de remisión proviene de ensayos del sistema SAINT® específicamente: el Sistema de Neuromodulación SAINT® de Magnus Medical, que usa su propio software de orientación por fMRI. Otros protocolos acelerados pueden copiar el calendario (10 sesiones al día, 5 días, patrón de pulsos iTBS) pero no usan el mismo sistema de orientación autorizado por la FDA. Los resultados del ensayo de SAINT® no se trasladan a esos protocolos. Pida a cada clínica sus propios resultados publicados.",
  },
  {
    question: "¿Qué cambia en el cerebro antes y después de la TMS?",
    answer:
      "Las resonancias funcionales (fMRI) en los estudios de SAINT® mostraron cambios en la conectividad cerebral tras el tratamiento. El enfoque de orientación se basa en una anticorrelación entre dos regiones cerebrales: la corteza cingulada subgenual anterior (sgACC) y la corteza prefrontal dorsolateral (DLPFC). Después de SAINT®, los investigadores observaron una normalización en los patrones de conectividad asociados con la depresión. Estos hallazgos de imagen provienen del grupo de investigación de SAINT® y pueden no aplicarse a otros protocolos de TMS.",
  },
];

export const esSaintTmsSuccessRates = makeEsArticle({
  slug: "saint-tms-success-rates",
  title: "Tasas de éxito de SAINT® TMS: lo que muestran los estudios (2026)",
  description:
    "79 % de remisión en el ensayo aleatorizado de 2022 (Cole et al., punto de 4 semanas). Conozca qué muestran los datos, por qué varían los resultados y por qué los de SAINT® no se trasladan a la TMS acelerada de 5 días sin autorización.",
  h1: "Tasas de éxito de SAINT® TMS: lo que muestra la investigación",
  breadcrumbLabel: "Tasas de éxito de SAINT® TMS",
  shortAnswer:
    "En el ensayo aleatorizado de 2022, el 79 % de los participantes alcanzó remisión tras el protocolo de SAINT® de cinco días (Cole et al., Am J Psychiatry 2022;179(2):132–141). Los resultados individuales pueden variar. Esa cifra proviene de ensayos del sistema SAINT® específicamente (el Sistema de Neuromodulación SAINT® de Magnus Medical, autorizado por la FDA) y no se traslada a otros protocolos acelerados que copian el calendario pero no el sistema de orientación. Si está en crisis, llame o envíe un texto al 988.",
  keyTakeaways: [
    "El 79 % de los participantes en el ensayo aleatorizado de 2022 alcanzó remisión tras cinco días (Cole et al., 2022). Los resultados individuales pueden variar.",
    "Remisión significa una puntuación de depresión por debajo de un umbral en una escala estandarizada, no solo sentirse mejor.",
    "Los estudios abiertos reportaron tasas similares, pero el ensayo aleatorizado es la evidencia más sólida.",
    "Las tasas de remisión de la TMS estándar varían entre aproximadamente 30–40 % en estudios publicados; SAINT® difiere en calendario, orientación y evidencia.",
    "La cifra del 79 % se aplica solo al sistema SAINT®, no a otros protocolos acelerados.",
    "Si está en crisis, llame o envíe un texto al 988.",
  ],
  sections: [
    {
      heading: "Tasas de éxito de SAINT® TMS: respuesta rápida",
      paragraphs: [
        "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) está autorizado por la FDA para el trastorno depresivo mayor (TDM) en adultos cuya depresión no ha mejorado lo suficiente con medicación antidepresiva previa. La tasa de éxito más citada proviene del ensayo aleatorizado doble ciego de 2022: el 79 % de los participantes alcanzó remisión tras el protocolo de cinco días (Cole et al., Am J Psychiatry 2022;179(2):132–141). Los resultados individuales pueden variar.",
        "Esa cifra del 79 % es específica del sistema SAINT® (el Sistema de Neuromodulación SAINT® de Magnus Medical, autorizado por la FDA, que usa software propietario de orientación por fMRI). Otros protocolos acelerados pueden copiar el calendario de tratamiento pero no usan el mismo sistema de orientación, y los resultados del ensayo de SAINT® no se trasladan a ellos. NWCOA es un proveedor verificado de SAINT® en Coral Springs, Florida. [[Conozca más sobre SAINT®|/es/saint]] o [[verifique si es candidato|/am-i-a-candidate-screener]].",
      ],
    },
    {
      heading: "Resultados abiertos vs aleatorizados",
      paragraphs: [
        "El programa de investigación de SAINT® en Stanford produjo varios estudios antes del ensayo pivotal de 2022. Los estudios previos eran abiertos: los participantes y los investigadores sabían que recibían el tratamiento real. Los estudios abiertos reportaron tasas de remisión de aproximadamente 78–80 % o más. Estos resultados fueron alentadores y justificaron un ensayo más amplio y riguroso.",
        "El estudio de 2022 fue un ensayo aleatorizado doble ciego, el estándar de oro en investigación clínica. Los participantes se asignaron al azar a SAINT® real o a un control simulado (placebo), y ni ellos ni los evaluadores sabían a qué grupo pertenecían. En ese ensayo, el 79 % de los participantes que recibieron SAINT® alcanzó remisión, frente al 13 % del grupo simulado (Cole et al., Am J Psychiatry 2022;179(2):132–141). Los resultados individuales pueden variar.",
        "El ensayo aleatorizado es la evidencia publicada más sólida porque controla el efecto placebo y el sesgo del investigador. Cuando alguien pregunta por la tasa de éxito de SAINT®, la cifra del 79 % del ensayo aleatorizado de 2022 es la respuesta más fiable.",
      ],
    },
    {
      heading: "Respuesta vs remisión: qué significa cada una",
      paragraphs: [
        "Dos términos aparecen en la investigación del tratamiento de la depresión: respuesta y remisión. Miden niveles distintos de mejoría, y la distinción importa.",
        "Respuesta significa una mejoría notable en los síntomas de depresión, típicamente una caída del 50 % o más en la puntuación de una escala de ánimo estandarizada. Un respondedor se siente mejor, pero puede seguir teniendo síntomas significativos. En los estudios de SAINT®, las tasas de respuesta fueron incluso más altas que las de remisión.",
        "La remisión es más estricta. Significa que la puntuación de depresión cae por debajo de un umbral; en los ensayos de SAINT®, una puntuación de 10 o menos en la Escala de Depresión Montgomery-Åsberg (MADRS). La remisión sugiere que la depresión ya no cumple el umbral clínico de un episodio depresivo mayor. La cifra del 79 % del ensayo de 2022 se refiere a la remisión, no solo a la respuesta. Los resultados individuales pueden variar.",
      ],
    },
    {
      heading: "Tasas de éxito de SAINT® vs TMS estándar y antidepresivos",
      paragraphs: [
        "Para contextualizar la tasa de remisión del 79 %, conviene compararla con otros tratamientos de la depresión. La TMS estándar (una sesión al día durante unas 6 semanas) tiene tasas de remisión publicadas que varían entre aproximadamente 30 % y 40 % en distintos estudios. La medicación antidepresiva tiene tasas más bajas, especialmente en la depresión resistente al tratamiento.",
        "El estudio STAR*D, un gran ensayo financiado con fondos federales sobre medicaciones antidepresivas, encontró que después de un antidepresivo, aproximadamente el 28 % de los participantes alcanzó remisión. Después de dos medicaciones, la tasa bajó aún más. Para cuando los pacientes habían probado varias medicaciones sin éxito (la población para la que SAINT® está diseñado), las probabilidades de remisión con la siguiente medicación sola eran bastante bajas.",
        "SAINT® está diseñado para adultos cuya depresión no ha mejorado lo suficiente con medicación antidepresiva previa. La tasa de remisión del 79 % en el ensayo de 2022 se obtuvo en esa población resistente al tratamiento (Cole et al., 2022). Los resultados individuales pueden variar. La comparación no versa sobre si un tratamiento es universalmente mejor, sino sobre qué evidencia se aplica a qué sistema y a qué grupo de pacientes.",
      ],
    },
    {
      heading: "Tasas de éxito de SAINT® comparadas con otros tratamientos",
      table: {
        headers: [
          "Tratamiento",
          "Tasa de respuesta",
          "Tasa de remisión",
          "Fuente",
        ],
        rows: [
          [
            "SAINT® (ensayo aleatorizado de 2022)",
            "No reportada por separado; la remisión fue el objetivo principal",
            "79 %",
            "Cole et al., Am J Psychiatry 2022;179(2):132–141",
          ],
          [
            "SAINT® (grupo control simulado)",
            "—",
            "13 %",
            "Cole et al., Am J Psychiatry 2022;179(2):132–141",
          ],
          [
            "TMS estándar (diversos estudios)",
            "Varía; típicamente 50–60 %",
            "Aproximadamente 30–40 %",
            "Literatura publicada sobre TMS; las tasas varían por estudio y población",
          ],
          [
            "Primer antidepresivo (STAR*D nivel 1)",
            "47 %",
            "28 %",
            "Trivedi et al., Am J Psychiatry 2006;163(11):1905–1917",
          ],
          [
            "Segundo antidepresivo (STAR*D nivel 2)",
            "31 %",
            "18 %",
            "Rush et al., Am J Psychiatry 2006;163(11):1905–1917",
          ],
        ],
      },
    },
    {
      heading: "Qué cambia en el cerebro: antes y después de la TMS",
      paragraphs: [
        "El enfoque de orientación de SAINT® se basa en imágenes cerebrales. Los investigadores usan resonancia funcional (fMRI) para identificar un objetivo personalizado de estimulación en cada persona. El objetivo se elige a partir de una anticorrelación entre dos regiones cerebrales: la corteza cingulada subgenual anterior (sgACC), a menudo sobreactiva en la depresión, y la corteza prefrontal dorsolateral (DLPFC), el área estimulada durante la TMS.",
        "Anticorrelación significa que cuando una región está más activa, la otra tiende a estarlo menos. En la depresión, este tira y afloja normal entre las dos regiones puede estar alterado. SAINT® busca estimular la ubicación específica de la DLPFC con la anticorrelación más fuerte con la sgACC en cada individuo: el punto donde la estimulación tiene más probabilidad de calmar la región sobreactiva.",
        "Después de SAINT®, el grupo de investigación de Stanford reportó cambios en la conectividad cerebral visibles en la fMRI: patrones asociados con la depresión parecieron normalizarse. Estos hallazgos de imagen provienen del programa de investigación de SAINT® y se aplican al sistema de orientación de SAINT®. No necesariamente se trasladan a otros protocolos de TMS que usan métodos de orientación distintos. [[Conozca la TMS guiada por fMRI|/es/learn/fmri-guided-tms-for-depression]].",
      ],
    },
    {
      heading: "Una historia de paciente",
      paragraphs: [
        "[ESPACIO PARA HISTORIA DE PACIENTE: se añadirá una historia real de paciente con consentimiento firmado y autorización HIPAA. No se inventará ninguna historia de paciente. Los resultados individuales pueden variar.]",
      ],
    },
    {
      heading: "¿Se aplican estos resultados a usted?",
      paragraphs: [
        "La tasa de remisión del 79 % proviene de un ensayo específico de un sistema específico. Se aplica al Sistema de Neuromodulación SAINT® de Magnus Medical, usado en adultos con trastorno depresivo mayor cuya depresión no había mejorado lo suficiente con medicación antidepresiva previa. NWCOA trata a adultos de 22 años o más.",
        "SAINT® está autorizado por la FDA para el TDM en adultos. No está autorizado para ansiedad, TOC, TEPT, trastorno bipolar ni ninguna otra condición. Si tiene depresión resistente al tratamiento y quiere saber si SAINT® puede ser adecuado para usted, el primer paso es una consulta gratuita. [[Verifique si es candidato|/am-i-a-candidate-screener]] o [[verifique los beneficios de su seguro|/check-insurance-options]]. SAINT® en NWCOA comienza en $25,000; la cobertura varía según el plan y verificamos sus beneficios sin costo.",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for treatment-resistant depression. Am J Psychiatry. 2020;177(8):716-726.",
      url: "https://pubmed.ncbi.nlm.nih.gov/32463539/",
    },
    {
      citation:
        "Cole EJ, Phillips AL, Bentzley BS, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for treatment-resistant depression. Am J Psychiatry. 2021;178(10):898-909.",
      url: "https://pubmed.ncbi.nlm.nih.gov/34256788/",
    },
    {
      citation:
        "Trivedi MH, et al. Evaluation of outcomes with citalopram for depression using STAR*D. Am J Psychiatry. 2006;163(11):1905-1917.",
      url: "https://pubmed.ncbi.nlm.nih.gov/17074942/",
    },
    {
      citation:
        "Rush AJ, et al. Acute and longer-term outcomes in STAR*D. Am J Psychiatry. 2006;163(11):1905-1917.",
      url: "https://pubmed.ncbi.nlm.nih.gov/17074943/",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — Magnus Neuromodulation System (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
  ],
  relatedLinks: successRateRelated,
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "octubre de 2026",
    lastReviewed: "octubre de 2026",
    lastUpdated: "octubre de 2026",
  },
  toc: [
    {
      label: "Respuesta rápida",
      href: "#tasas-de-exito-de-saint-tms-respuesta-rapida",
    },
    {
      label: "Abiertos vs aleatorizados",
      href: "#resultados-abiertos-vs-aleatorizados",
    },
    {
      label: "Respuesta vs remisión",
      href: "#respuesta-vs-remision-que-significa-cada-una",
    },
    {
      label: "vs TMS estándar y antidepresivos",
      href: "#tasas-de-exito-de-saint-vs-tms-estandar-y-antidepresivos",
    },
    {
      label: "Tabla de comparación",
      href: "#tasas-de-exito-de-saint-comparadas-con-otros-tratamientos",
    },
    {
      label: "Cerebro antes y después de la TMS",
      href: "#que-cambia-en-el-cerebro-antes-y-despues-de-la-tms",
    },
    {
      label: "Historia de paciente",
      href: "#una-historia-de-paciente",
    },
    {
      label: "¿Se aplican a usted?",
      href: "#se-aplican-estos-resultados-a-usted",
    },
    { label: "Preguntas frecuentes", href: "#faq" },
    { label: "Investigación citada", href: "#cited-research" },
  ],
  safetyBlock:
    "SAINT® no es para todos. Está contraindicado con metal magnético no removible cerca de la cabeza, ciertos dispositivos implantados o presión intracraneal elevada. Posibles efectos: cefalea, fatiga, molestia en el cuero cabelludo y ansiedad; la TMS conlleva un riesgo bajo de convulsión y, como en cualquier tratamiento de la depresión, riesgo de empeoramiento o pensamientos suicidas. Si está en crisis, llame o envíe un texto al 988.",
  trademarkFooter:
    "SAINT® es una marca registrada de Stanford University, licenciada en exclusiva a Magnus Medical, Inc. NWCOA no está afiliado a Stanford University.",
  ctaBand: {
    headline: "Vea si SAINT® es una opción, sin costo",
    primaryText: "Evaluar mi candidatura",
    primaryHref: "/am-i-a-candidate-screener",
    secondaryText: "Verificar mi seguro",
    secondaryHref: "/check-insurance-options",
    phone: "954-827-9793",
  },
});
